import { Check, Plus, Replace } from "lucide-react";
import { useMemo, useState } from "react";
import { PlantArt } from "../../components/PlantArt";
import { catalogById } from "../../data/catalog";
import type { BalconyProfile, CatalogItem, CatalogType, DesignItem } from "../../data/model";
import { compatibleCatalog } from "../../lib/compatibility";
import { formatMoney } from "../../lib/format";

interface Props {
  profile: BalconyProfile;
  design: DesignItem[];
  replaceMode: boolean;
  onAdd: (item: CatalogItem) => void;
}

const types: Array<[CatalogType | "all", string]> = [
  ["all", "Tất cả"],
  ["plant", "Cây"],
  ["pot", "Chậu"],
  ["furniture", "Nội thất"],
  ["accessory", "Phụ kiện"],
];

export function CatalogPanel({ profile, design, replaceMode, onAdd }: Props) {
  const [type, setType] = useState<CatalogType | "all">("all");
  const items = useMemo(
    () => compatibleCatalog(profile).filter((item) => type === "all" || item.type === type),
    [profile, type],
  );
  const existingRefs = new Set(design.map((item) => item.refId));

  return (
    <div className="catalog-panel">
      <div className="catalog-heading">
        <div><span className="eyebrow">{replaceMode ? "Chọn món thay thế" : "Thư viện hợp điều kiện"}</span><h2>{replaceMode ? "Thay món đang chọn" : "Thêm vào bản phác"}</h2></div>
        {replaceMode && <span className="replace-pill"><Replace size={14} /> Đang thay</span>}
      </div>
      <div className="catalog-tabs" role="tablist" aria-label="Loại hạng mục">
        {types.map(([value, label]) => (
          <button type="button" role="tab" aria-selected={type === value} key={value} onClick={() => setType(value)}>{label}</button>
        ))}
      </div>
      {items.length ? (
        <div className="catalog-grid">
          {items.map((item) => (
            <article className="catalog-card" key={item.id}>
              <div className={`catalog-art type-${item.type}`}>
                {item.type === "plant" ? <PlantArt mediaKey={item.mediaKey} label={item.name} /> : <span aria-hidden="true">{item.type === "pot" ? "◒" : item.type === "furniture" ? "⌑" : "✦"}</span>}
              </div>
              <div>
                <span>{item.type === "plant" ? item.scientificName : item.description}</span>
                <h3>{item.name}</h3>
                <strong>{formatMoney(item.unitPrice)}</strong>
              </div>
              <button className="catalog-add" type="button" onClick={() => onAdd(item)} aria-label={`${replaceMode ? "Thay bằng" : "Thêm"} ${item.name}`}>
                {existingRefs.has(item.id) && !replaceMode ? <><Check aria-hidden="true" /> Thêm nữa</> : <><Plus aria-hidden="true" /> {replaceMode ? "Dùng món này" : "Thêm"}</>}
              </button>
            </article>
          ))}
        </div>
      ) : (
        <div className="catalog-empty"><p>Không còn món tương thích để thêm vào vùng này.</p><button type="button" onClick={() => setType("all")}>Xem tất cả loại</button></div>
      )}
      <small className="catalog-note">Thư viện chỉ hiện hạng mục qua bộ lọc nắng, chăm, gió và an toàn hiện tại.</small>
      <div className="sr-only" aria-live="polite">
        {design.length} dòng hạng mục đang có. {Array.from(existingRefs).map((id) => catalogById.get(id)?.name).filter(Boolean).join(", ")}
      </div>
    </div>
  );
}
